import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import type { Subtask } from "@/hooks/use-categories";
import {
  useCategory,
  useDeleteCategory,
  useDeleteSubtask,
  useReorderSubtasks,
  useUpdateCategory,
  useUpdateSubtask,
} from "@/hooks/use-categories";
import { useJobTypes } from "@/hooks/use-job-types";
import type { UpdateCategoryInput } from "@/schemas/categories";
import { UpdateCategorySchema } from "@/schemas/categories";
import { DeactivateEntityDialog } from "./DeactivateEntityDialog";
import { DeleteEntityDialog } from "./DeleteEntityDialog";
import { EditSubtaskDialog, NewSubtaskDialog } from "./SubtaskDialogs";

type CatDialogState = { type: "none" } | { type: "deactivate" } | { type: "delete" };
type SubtaskDialogState =
  | { type: "none" }
  | { type: "new" }
  | { type: "edit"; subtask: Subtask }
  | { type: "deactivate"; subtask: Subtask }
  | { type: "delete"; subtask: Subtask };

interface CategoryDetailProps {
  categoryId: string;
}

export function CategoryDetail({ categoryId }: CategoryDetailProps) {
  const navigate = useNavigate();
  const { data: detail, isLoading, isError, error, refetch } = useCategory(categoryId);
  const { data: jobTypes = [] } = useJobTypes();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const updateSubtask = useUpdateSubtask();
  const deleteSubtask = useDeleteSubtask();
  const reorderSubtasks = useReorderSubtasks();

  const [catDialog, setCatDialog] = useState<CatDialogState>({ type: "none" });
  const [subtaskDialog, setSubtaskDialog] = useState<SubtaskDialogState>({ type: "none" });
  const [showInactiveSubtasks, setShowInactiveSubtasks] = useState(false);

  const form = useForm<UpdateCategoryInput>({
    resolver: zodResolver(UpdateCategorySchema),
    defaultValues: { name: "", description: "", jobTypeIds: [] },
  });

  useEffect(() => {
    if (detail) {
      form.reset({
        name: detail.name,
        description: detail.description ?? "",
        jobTypeIds: detail.tags.map((t) => t.jobTypeId),
      });
    }
  }, [detail, form]);

  function toggleJobType(id: string) {
    const current = form.getValues("jobTypeIds") ?? [];
    if (current.includes(id)) {
      form.setValue(
        "jobTypeIds",
        current.filter((x) => x !== id),
        { shouldValidate: true },
      );
    } else {
      form.setValue("jobTypeIds", [...current, id], { shouldValidate: true });
    }
  }

  function onSubmit(values: UpdateCategoryInput) {
    updateCategory.mutate({ id: categoryId, ...values });
  }

  function handleDeactivateConfirm() {
    updateCategory.mutate(
      { id: categoryId, active: false },
      { onSuccess: () => setCatDialog({ type: "none" }) },
    );
  }

  function handleDeleteConfirm() {
    deleteCategory.mutate(categoryId, {
      onSuccess: () => {
        setCatDialog({ type: "none" });
        navigate({ to: "/dashboard/settings/categories" });
      },
    });
  }

  function handleSubtaskDeactivate(subtask: Subtask) {
    updateSubtask.mutate(
      { id: subtask.id, active: false },
      { onSuccess: () => setSubtaskDialog({ type: "none" }) },
    );
  }

  function handleSubtaskDelete(subtask: Subtask) {
    deleteSubtask.mutate(subtask.id, {
      onSuccess: () => setSubtaskDialog({ type: "none" }),
    });
  }

  function moveSubtaskUp(index: number) {
    if (!detail || index === 0) return;
    const ids = detail.subtasks.map((s) => s.id);
    const prev = ids[index - 1];
    const curr = ids[index];
    if (!prev || !curr) return;
    const newOrder = [...ids];
    newOrder[index - 1] = curr;
    newOrder[index] = prev;
    reorderSubtasks.mutate({ categoryId, orderedSubtaskIds: newOrder });
  }

  function moveSubtaskDown(index: number) {
    if (!detail || index >= detail.subtasks.length - 1) return;
    const ids = detail.subtasks.map((s) => s.id);
    const next = ids[index + 1];
    const curr = ids[index];
    if (!next || !curr) return;
    const newOrder = [...ids];
    newOrder[index + 1] = curr;
    newOrder[index] = next;
    reorderSubtasks.mutate({ categoryId, orderedSubtaskIds: newOrder });
  }

  const selectedJobTypeIds = form.watch("jobTypeIds") ?? [];
  const activeJobTypes = jobTypes.filter((jt) => jt.active);

  if (isLoading) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <Link to="/dashboard/settings/categories">Activity Categories</Link>
            <ChevronRight size={14} />
            <strong>Loading…</strong>
          </div>
        </div>
        <div className="page">
          <div className="card">
            <div className="card__body space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-48" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isError || !detail) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <Link to="/dashboard/settings/categories">Activity Categories</Link>
            <ChevronRight size={14} />
            <strong>Error</strong>
          </div>
        </div>
        <div className="page">
          <ErrorState
            message={error?.message ?? "Failed to load category"}
            onRetry={() => refetch()}
          />
        </div>
      </>
    );
  }

  const visibleSubtasks = showInactiveSubtasks
    ? detail.subtasks
    : detail.subtasks.filter((s) => s.active);
  const inactiveCount = detail.subtasks.filter((s) => !s.active).length;

  return (
    <>
      <div className="topbar">
        <div className="topbar__crumbs">
          <Link to="/dashboard/settings/categories">Activity Categories</Link>
          <ChevronRight size={14} />
          <strong>{detail.name}</strong>
        </div>
      </div>

      <div className="page">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/settings/categories"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft size={16} />
              Back
            </Link>
            <h1 className="page-header__title">{detail.name}</h1>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                detail.active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"
              }`}
            >
              {detail.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Category edit form */}
        <div className="card mb-4">
          <div className="card__header">
            <h2 className="h-section">Details</h2>
          </div>
          <div className="card__body">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add a description for this activity category…"
                          className="resize-none"
                          rows={3}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="jobTypeIds"
                  render={() => (
                    <FormItem>
                      <FormLabel>Job Types</FormLabel>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {activeJobTypes.map((jt) => {
                          const selected = selectedJobTypeIds.includes(jt.id);
                          return (
                            <button
                              key={jt.id}
                              type="button"
                              onClick={() => toggleJobType(jt.id)}
                              className={`chip-jobtype chip-jobtype--${jt.code} cursor-pointer transition-opacity ${
                                selected
                                  ? "ring-2 ring-offset-1 ring-ring opacity-100"
                                  : "opacity-50"
                              }`}
                              title={jt.label}
                            >
                              {jt.code}
                            </button>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-2">
                    <Button type="submit" size="sm" disabled={updateCategory.isPending}>
                      {updateCategory.isPending ? "Saving…" : "Save Changes"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        form.reset({
                          name: detail.name,
                          description: detail.description ?? "",
                          jobTypeIds: detail.tags.map((t) => t.jobTypeId),
                        })
                      }
                    >
                      Reset
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {detail.active ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCatDialog({ type: "deactivate" })}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={updateCategory.isPending}
                        onClick={() => updateCategory.mutate({ id: categoryId, active: true })}
                      >
                        Reactivate
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setCatDialog({ type: "delete" })}
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>

        {/* Subtasks section */}
        <div className="card">
          <div className="card__header flex items-center justify-between">
            <h2 className="h-section">
              Subtasks{" "}
              <span className="text-muted-foreground font-normal text-sm">
                ({detail.subtasks.filter((s) => s.active).length} active)
              </span>
            </h2>
            <Button size="sm" onClick={() => setSubtaskDialog({ type: "new" })}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add Subtask
            </Button>
          </div>
          <div className="card__body p-0">
            {detail.subtasks.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                No subtasks yet. Add one to get started.
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-24">
                        Status
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide w-48">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSubtasks.map((subtask, index) => (
                      <tr
                        key={subtask.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={
                              subtask.active
                                ? "text-foreground"
                                : "text-muted-foreground line-through"
                            }
                          >
                            {subtask.name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              subtask.active
                                ? "bg-green-100 text-green-800"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {subtask.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={index === 0 || reorderSubtasks.isPending}
                              onClick={() => moveSubtaskUp(index)}
                              title="Move up"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={
                                index === visibleSubtasks.length - 1 || reorderSubtasks.isPending
                              }
                              onClick={() => moveSubtaskDown(index)}
                              title="Move down"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setSubtaskDialog({ type: "edit", subtask })}
                              title="Rename"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {subtask.active ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground"
                                onClick={() => setSubtaskDialog({ type: "deactivate", subtask })}
                                title="Deactivate"
                              >
                                Off
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground"
                                disabled={updateSubtask.isPending}
                                onClick={() =>
                                  updateSubtask.mutate({ id: subtask.id, active: true })
                                }
                                title="Activate"
                              >
                                On
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => setSubtaskDialog({ type: "delete", subtask })}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {inactiveCount > 0 && (
                  <div className="px-4 py-2 border-t border-border">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowInactiveSubtasks((v) => !v)}
                    >
                      {showInactiveSubtasks
                        ? "Hide inactive subtasks"
                        : `Show ${inactiveCount} inactive subtask${inactiveCount !== 1 ? "s" : ""}`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Category dialogs */}
      {catDialog.type === "deactivate" && (
        <DeactivateEntityDialog
          open
          onOpenChange={(open) => !open && setCatDialog({ type: "none" })}
          entityType="category"
          entityId={categoryId}
          entityName={detail.name}
          isPending={updateCategory.isPending}
          onConfirm={handleDeactivateConfirm}
        />
      )}

      {catDialog.type === "delete" && (
        <DeleteEntityDialog
          open
          onOpenChange={(open) => !open && setCatDialog({ type: "none" })}
          entityType="category"
          entityName={detail.name}
          isPending={deleteCategory.isPending}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Subtask dialogs */}
      {subtaskDialog.type === "new" && (
        <NewSubtaskDialog
          open
          onOpenChange={(open) => !open && setSubtaskDialog({ type: "none" })}
          categoryId={categoryId}
          categoryName={detail.name}
        />
      )}

      {subtaskDialog.type === "edit" && (
        <EditSubtaskDialog
          key={subtaskDialog.subtask.id}
          open
          onOpenChange={(open) => !open && setSubtaskDialog({ type: "none" })}
          subtask={subtaskDialog.subtask}
        />
      )}

      {subtaskDialog.type === "deactivate" && (
        <DeactivateEntityDialog
          open
          onOpenChange={(open) => !open && setSubtaskDialog({ type: "none" })}
          entityType="subtask"
          entityId={subtaskDialog.subtask.id}
          entityName={subtaskDialog.subtask.name}
          isPending={updateSubtask.isPending}
          onConfirm={() => handleSubtaskDeactivate(subtaskDialog.subtask)}
        />
      )}

      {subtaskDialog.type === "delete" && (
        <DeleteEntityDialog
          open
          onOpenChange={(open) => !open && setSubtaskDialog({ type: "none" })}
          entityType="subtask"
          entityName={subtaskDialog.subtask.name}
          isPending={deleteSubtask.isPending}
          onConfirm={() => handleSubtaskDelete(subtaskDialog.subtask)}
        />
      )}
    </>
  );
}
